import {
  FEATURE_DISABLED_MESSAGE,
  getFeatureDisabledKey,
  getLocalStorageData,
  getURLS,
} from "@picovico/utils";

import { urlList } from "@picovico/utils/@types";
import axios from "axios";
import { getTenantDomain } from "../tenant";

export type AxiosRequestExtraConfig = {
  name?: string;
  idx?: string;
  /**
   * Set on a read-only lookup that fills an optional picker or badge (the menu
   * editors' inventory lists): when it is refused with 403 `FEATURE_DISABLED`
   * the caller falls back to an empty list on its own, so the global
   * "feature not enabled" notice is not raised for it.
   */
  silentFeatureDisabled?: boolean;
};

export type AxiosCustomError = {
  data: Record<string, object | string | Array<string | object> | number>;
  message: string;
  status: number;
};

declare module "axios" {
  export interface AxiosRequestConfig extends AxiosRequestExtraConfig { }
}

export const axiosBaseUrl = () => {
  const tenantDomain = getTenantDomain();
  if (tenantDomain) {
    if (process.env.NODE_ENV === "production") {
      return `https://${tenantDomain}/api`;
    } else {
      return `${process.env.NEXT_PUBLIC_TENANT_PROTOCOL || "https"}://${tenantDomain}/api`;
    }
  }
  return process.env.NEXT_PUBLIC_BASE_API_URL;
};

export const axiosService = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_API_URL,
  timeout: 1000 * 60,
});

export function setAxiosAuthInterceptor({
  apiRoutes,
  source = "panel",
}: {
  apiRoutes: urlList;
  source?: "admin" | "web" | "panel";
}) {
  axiosService.interceptors.request.use((config) => {
    if (config.name) {
      config.url = getURLS(apiRoutes, config.name, config?.idx);
    }
    const token = getLocalStorageData("token");
    const deviceId = getLocalStorageData("device_id");
    const tenantDomain = getTenantDomain();
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    if (deviceId) {
      config.headers.deviceId = deviceId;
    }

    if (source === "admin") {
      const domain = getLocalStorageData("domain");
      const cinemaHall = getLocalStorageData("cinema_hall");

      if (tenantDomain) {
        config.baseURL = axiosBaseUrl();
      }

      if (domain) {
        config.headers.domain = domain;
      }
      if (cinemaHall && !config.headers.cinemaHall) {
        config.headers.cinemaHall = cinemaHall;
      }
    }

    if (source === "web") {
      const companyIdx = getLocalStorageData("companyIdx");
      if (companyIdx) {
        config.headers.companyIdx = companyIdx;
      }
      const cinemaHall = getLocalStorageData("cinema_hall");
      if (tenantDomain) {
        config.baseURL = axiosBaseUrl();
      }
      if (
        cinemaHall &&
        (config.name === "social_login_initiate" ||
          config.name === "social_login_verify")
      ) {
        config.headers.cinemaHall = cinemaHall;
      }
    }

    return config;
  });
}

export type FeatureDisabledListener = (
  feature: string,
  message: string,
) => void;

let featureDisabledListener: FeatureDisabledListener | null = null;

/**
 * Register (or clear, with null) the one function told about a 403
 * `FEATURE_DISABLED`: an endpoint of a feature the company has switched off
 * (claude/design/feature-flags/). The admin uses it to toast once.
 */
export function setFeatureDisabledListener(
  listener: FeatureDisabledListener | null,
) {
  featureDisabledListener = listener;
}

axiosService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const response = error.response;
    const feature = getFeatureDisabledKey(response);
    if (feature) {
      // The body is `{code, feature}` with no message, which every caller's
      // error toast would read as "something went wrong".
      if (response.data && typeof response.data === "object") {
        response.data.message ??= FEATURE_DISABLED_MESSAGE;
      }
      if (!error.config?.silentFeatureDisabled) {
        featureDisabledListener?.(feature, FEATURE_DISABLED_MESSAGE);
      }
    }
    return Promise.reject(response || error);
  },
);

export default axiosService;
